'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useContext, useEffect, useState } from 'react'

// walrus
import Box from '@components/kit/box'
import Card from '@components/kit/card'
import Loading from '@components/kit/loading'
import Text from '@components/kit/text'
import { Row } from '@components/kit/grid'

// lib
import { ChatContext } from '../chat.web'
import { useMedsStore } from '@store/Meds'
import { PharmacyInfo } from '@store/Meds/types'
import { ColumnWrapper, LabelText, ValueText } from '@components/table'

const RESOLUTION_TIMEOUT = 500

/* -----------------------------------------------------------------------------
 * Pharmacy
 * -------------------------------------------------------------------------- */

function Pharmacy() {
  const { userId } = useContext(ChatContext)
  const [pharmacies, setPharmacies] = useState<PharmacyInfo[]>([])
  const [isFetching, setIsFetching] = useState(true)

  const { getPharmacyInfo } = useMedsStore.getState()

  useEffect(() => {
    setIsFetching(true)
    getPharmacyInfo(userId)
      .then(data => {
        if (!data) {
          console.log('NO_PHARMACY_DATA')
          return
        }

        setPharmacies(data.pharmacies)
      })
      .catch(err => {
        console.log(err)
      })
      .finally(() => {
        setTimeout(() => {
          setIsFetching(false)
        }, RESOLUTION_TIMEOUT)
      })
  }, [])

  return (
    <React.Fragment>
      <Box flexDirection='column' alignItems='flex-start' mb='12'>
        <Row width='100%'>
          <Text ml='12' font='display.16.bold'>
            Pharmacy
          </Text>
        </Row>
        <Box mt='08' flexDirection='row' gap='04'>
          {/* for future implementations: nav tools */}
        </Box>
        {isFetching && (
          <Card
            width='100%'
            minHeight='75px'
            maxHeight='200px'
            mt='08'
            px='00'
            py='00'
            mb='12'
            flex='1'
            justifyContent='center'
            alignItems='center'
          >
            <Box position='fixed'>
              <Loading />
            </Box>
          </Card>
        )}
        {pharmacies.length === 0 && !isFetching && (
          <Card width='100%' mt='08' px='00' py='00' mb='12'>
            <Row flex='1' width='100%'>
              <Text ml='12' font='text.14.medium' margin='16'>
                No pharmacy information available for this member.
              </Text>
            </Row>
          </Card>
        )}
        {pharmacies.map((pharmacy, index) => (
          <Card width='100%' mt='08' px='00' py='00' mb='12' key={index}>
            <Row flex='1' width='100%'>
              <ColumnWrapper width='70%'>
                <LabelText>Name</LabelText>
                <ValueText>{pharmacy?.name || '-'}</ValueText>
              </ColumnWrapper>
              <ColumnWrapper width='30%'>
                <LabelText>Phone</LabelText>
                <ValueText>{pharmacy?.phone_number || '-'}</ValueText>
              </ColumnWrapper>
            </Row>
            <Row flex='1' width='100%'>
              <ColumnWrapper width='70%'>
                <LabelText>Address</LabelText>
                <ValueText>{pharmacy?.address || '-'}</ValueText>
              </ColumnWrapper>
              <ColumnWrapper width='30%'>
                <LabelText>City</LabelText>
                <ValueText>{pharmacy?.city || '-'}</ValueText>
              </ColumnWrapper>
            </Row>
            {pharmacy.prescribers.map((prescriber, index) => (
              <Row flex='1' width='100%'>
                <ColumnWrapper width='70%'>
                  <LabelText>Prescriber</LabelText>
                  <ValueText>{prescriber?.name || '-'}</ValueText>
                </ColumnWrapper>
                <ColumnWrapper width='30%'>
                  <LabelText>Prescriber NPI</LabelText>
                  <ValueText>{prescriber?.npi || '-'}</ValueText>
                </ColumnWrapper>
              </Row>
            ))}
          </Card>
        ))}
      </Box>
    </React.Fragment>
  )
}

export default Pharmacy
